package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

@Serializable
enum class QrSessionStatus { ACTIVE, ENDED }

/**
 * 키오스크 세션. `edubridge_academies/{academyId}/qrSessions/{sessionId}`(§3.2).
 *
 * **클라이언트는 이 컬렉션을 읽지도 쓰지도 못한다**(rules `read, write: if false`).
 * 스태프에게 read를 허용하면 모든 스태프가 원격에서 유효한 QR을 재현할 수 있고
 * nonce가 브라우저 캐시에 남는다. 콜러블 전용으로 두면 nonce는 응답 본문으로만 오간다(§3.3).
 *
 * 여기 노출되는 것은 콜러블이 돌려준 세션 메타뿐이고 `currentNonce`·`previousNonce`는 없다.
 */
@Serializable
data class QrSession(
    val sessionId: String,
    val academyId: String,
    /** "1층 로비 태블릿" 등 배치 위치. */
    val label: String,
    val status: QrSessionStatus,
    val rotationSeconds: Int = 20,
    /** 스캔~도달 지연 흡수 창(§3.4-3). 촬영본은 최대 `rotationSeconds + graceSeconds` 후 무효. */
    val graceSeconds: Int = 15,
    val createdBy: String? = null,
    val createdAt: String? = null,
    /** 회전 하트비트. 5분간 정체하면 태블릿이 죽은 것이다(R6). */
    val lastRotatedAt: String? = null,
    val endedAt: String? = null,
)

/**
 * 회전 토큰. 콜러블 응답 본문으로만 오간다 — **저장·로그 금지**.
 *
 * 이벤트에는 원문이 아니라 `nonceHash`만 남는다(§3.5).
 */
@Serializable
data class QrToken(
    val nonce: String,
    /** ISO-8601. 실제 허용 한계는 `expiresAt + graceSeconds`다. */
    val expiresAt: String,
    val rotationSeconds: Int,
)

/**
 * 스캔 거부 사유(§3.4).
 *
 * M2 스캐너는 이 4가지(EXPIRED / NOT_ENROLLED / COOLDOWN / DUPLICATE)를 각각 다른 문구로 보여야 한다.
 * 서버 콜러블의 에러 코드에 대응하지만 **예외가 아니라 반환값으로 전달한다** —
 * Swift 경계에서 uncaught Kotlin 예외는 프로세스를 죽인다.
 */
@Serializable
enum class ScanRejection {
    /** nonce 만료 또는 회전 창 이탈. 재사용 공격의 1차 방어(§3.4-3). */
    EXPIRED,

    /** 해당 학원의 ACTIVE STUDENT 멤버십이 아니다. 타 학원 학생·학부모·비회원 전부 여기(§3.4-4). */
    NOT_ENROLLED,

    /** `minScanIntervalSeconds` 이내 연속 스캔(§3.4-6). */
    COOLDOWN,

    /** 같은 `{uid, nonce}` 재스캔. 멱등 처리로 직전 결과가 함께 온다(§3.4-5). */
    DUPLICATE,

    /** 세션이 ENDED이다(§3.4-2). */
    SESSION_ENDED,

    /** 세션 문서 자체가 없다. 키오스크가 만료된 세션 ID로 렌더 중일 때(§3.4-2). */
    SESSION_NOT_FOUND,

    /**
     * ACTIVE STUDENT 멤버십은 있으나 `students/{id}.userId`로 연결된 학생 레코드가 없다.
     * 초대 수락 전이거나 학원이 계정 연결을 아직 하지 않은 상태(§3.4-4).
     */
    NO_STUDENT_RECORD,

    UNAUTHENTICATED,
    NETWORK,
    INTERNAL,
}

/** `scanAttendance` 입력(§3.3). */
@Serializable
data class ScanRequest(
    val academyId: String,
    val sessionId: String,
    /** QR 페이로드에서 읽은 nonce. 어디에도 저장하지 않는다. */
    val nonce: String,
    /** 클라이언트 스캔 시각(ISO-8601). 서버 판정의 근거가 아니라 지연 관측용이다. */
    val clientScannedAt: String? = null,
)

/**
 * 스캔 결과. 성공·거부를 모두 담는 단일 반환형이다.
 *
 * [rejection]이 null이면 성공이고 [eventId]·[status]가 채워진다.
 * DUPLICATE는 거부이면서도 직전 결과를 함께 돌려준다(멱등, §3.4-5).
 */
@Serializable
data class ScanResult(
    val eventId: String? = null,
    val status: AttendanceStatus? = null,
    val studentName: String? = null,
    val className: String? = null,
    val occurredAt: String? = null,
    val rejection: ScanRejection? = null,
    /** 사용자가 해결할 수 있는 한국어 문장. 공급자 코드를 그대로 노출하지 않는다(MVP §9). */
    val message: String? = null,
) {
    val isAccepted: Boolean get() = rejection == null && eventId != null
}

/** `createQrSession` 결과. 세션과 첫 토큰을 함께 돌려준다(§3.3). */
@Serializable
data class QrSessionResult(
    val session: QrSession? = null,
    val token: QrToken? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && session != null && token != null
}

/** `rotateQrToken` 결과. 키오스크가 `rotationSeconds`마다 호출한다. */
@Serializable
data class QrTokenResult(
    val token: QrToken? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && token != null
}
